import { useState } from "react";
import { Capacitor } from "@capacitor/core";
import { Share } from "@capacitor/share";
import { Link } from "react-router-dom";
import {
  Bell,
  Share2,
  ChevronRight,
  Crown,
  Download,
  Heart,
  Gift,
  Flower2,
  LayoutGrid,
  LineChart,
  Sparkles,
  Compass,
  LockKeyhole,
  Settings as SettingsIcon,
} from "lucide-react";
import PageContainer from "@/components/PageContainer";
import BrandHeader from "@/components/BrandHeader";
import { getJournalEntries, getProtectItems, getResults } from "@/lib/storage";
import { useAuth } from "@/context/AuthContext";
import { AMBIENT_SYMBOLS } from "@/lib/companionModes";

export default function You() {
  const { user } = useAuth();
  const [shareLinkVisible, setShareLinkVisible] = useState(false);
  const shareApp = async () => {
    const details = { title: "UnAnonymous", text: "Less harm. More connection.", url: "https://un-anonymous.com" };
    try {
      if (Capacitor.isNativePlatform()) {
        await Share.share({ ...details, dialogTitle: "Share UnAnonymous" });
      } else if (navigator.share) {
        await navigator.share(details);
      } else {
        setShareLinkVisible(true);
      }
    } catch (error) {
      if (error?.name !== "AbortError" && !/cancel/i.test(String(error?.message || ""))) setShareLinkVisible(true);
    }
  };
  const reflections = getResults().length;
  const journalEntries = getJournalEntries().length;
  const protectedCount = getProtectItems().length;

  return (
    <PageContainer className="pt-7" testid="you-page">
      <BrandHeader compact />

      <section className="mb-7">
        <p
          className="text-[10px] uppercase tracking-[0.34em] mb-2"
          style={{ color: "var(--un-text-3)", fontFamily: "Manrope, sans-serif" }}
        >
          Your private space
        </p>
        <h1
          className="text-[43px] leading-[1.02] tracking-[-0.03em]"
          style={{ color: "var(--un-plum-deep)", fontFamily: "Lora, serif" }}
        >
          A clearer picture
          <br />
          <em className="font-normal" style={{ color: "var(--un-burgundy)", fontFamily: "Cormorant Garamond, serif" }}>
            of what matters.
          </em>
        </h1>
        <p className="mt-4 text-[15px] leading-relaxed" style={{ color: "var(--un-text-2)" }}>
          Your private space can hold hard moments, good ones, patterns, meaning, and the directions you want to move toward.
        </p>
      </section>

      <section
        className="rounded-[1.75rem] p-5 mb-5"
        style={{ background: "linear-gradient(135deg, #4A2D48, #6D4866)", color: "#fffaf5" }}
      >
        <div className="flex items-center gap-3 mb-5">
          <div className="w-11 h-11 rounded-full flex items-center justify-center" style={{ background: "rgba(255,255,255,0.12)" }}>
            <Heart size={19} />
          </div>
          <div>
            <p className="text-[18px]" style={{ fontFamily: "Lora, serif" }}>What you’ve kept</p>
            <p className="text-[12px] opacity-70">Stored on this device</p>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2 text-center">
          <Stat value={reflections} label="Check-ins" />
          <Stat value={journalEntries} label="Entries" />
          <Stat value={protectedCount} label="Protected" />
        </div>
      </section>

      <section className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5" aria-label="Care and kindness">
        <AmbientCareCard
          symbol={AMBIENT_SYMBOLS.find((item) => item.id === "compassion")}
          icon={Gift}
          title="A little something for you"
          note="A treat, an experience, rest, or something caring for yourself. Care does not have to be earned."
        />
        <AmbientCareCard
          symbol={AMBIENT_SYMBOLS.find((item) => item.id === "nurture")}
          icon={Flower2}
          title="What would feel caring today?"
          note="Warmth, steadiness, softness, and being looked after can matter too."
        />
      </section>

      <div className="rounded-[1.75rem] overflow-hidden mb-5" style={{ background: "rgba(253,250,246,0.84)", border: "1px solid rgba(91,57,84,0.06)" }}>
        <YouLink to={user ? "/account" : "/auth?mode=create"} icon={LockKeyhole} title={user ? "Account" : "Optional account"} note={user ? user.email : "Optional sign-in identity across devices — core reflection still works without it"} />
        <YouLink to="/mosaic" icon={Sparkles} title="Soul Mosaic" note="Words, images, moments, and pieces you chose to keep" />
        <YouLink to="/progress" icon={LineChart} title="Patterns over time" note="Resets, moments, and patterns you chose to keep" />
        <YouLink to="/dashboard" icon={LayoutGrid} title="Saved reflections" note="Your check-ins, actions, and entries" />
        <YouLink to="/step-forward" icon={Compass} title="Alignment" note="Experience, Learning & Growth, Contribution and Source" />
        <YouLink to="/streaks" icon={Heart} title="Gentle rhythms" note="Notice the care you return to, without a score" />
        <YouLink to="/practices" icon={Compass} title="Step Forward practices" note="Your existing guided practices" />
        <YouLink to="/settings" icon={Bell} title="Gentle nudges" note="Choose how the app may whisper" />
        <YouLink to="/backup" icon={Download} title="Backup & export" note="Keep a private copy of your words" />
        <YouLink to="/living-book" icon={Heart} title="Living Book" note="Chapters, people, wishes and words to keep" />
        <YouLink to="/premium" icon={Crown} title="Premium" note="Full reset library, unlimited Toolkit, and more ways to reflect" />
        <button type="button" onClick={shareApp} className="w-full text-left flex items-center gap-3 px-5 py-4 un-press" style={{ borderBottom: "1px solid rgba(91,57,84,0.07)" }}>
          <span className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ background: "rgba(91,57,84,0.06)", color: "var(--un-plum)" }}><Share2 size={17} aria-hidden="true" /></span>
          <span className="flex-1"><span className="block text-[14px] font-medium" style={{ color: "var(--un-text-1)" }}>Share UnAnonymous</span><span className="block text-[11px]" style={{ color: "var(--un-text-3)" }}>Share the app with someone you care about</span></span>
          <ChevronRight size={16} aria-hidden="true" style={{ color: "var(--un-text-3)" }} />
        </button>
        {shareLinkVisible && <div className="px-5 py-4" role="status"><label htmlFor="share-app-link" className="block text-[12px] mb-2">Copy this link to share UnAnonymous</label><input id="share-app-link" readOnly value="https://un-anonymous.com" onFocus={event => event.target.select()} className="w-full p-3 rounded-xl text-[14px]" /><button type="button" onClick={() => setShareLinkVisible(false)} className="mt-3 underline text-[13px]">Close</button></div>}
        <YouLink to="/privacy" icon={LockKeyhole} title="Privacy" note="What stays here and what gets processed" last />
      </div>

      <div className="rounded-3xl p-5 mb-8" style={{ background: "rgba(91,57,84,0.05)" }}>
        <div className="flex gap-3">
          <SettingsIcon size={17} className="mt-0.5" style={{ color: "var(--un-plum)" }} />
          <p className="text-[12px] leading-relaxed" style={{ color: "var(--un-text-3)" }}>
            An account is optional. Account identity is separate from device-first reflection content. Features that use external processing tell you before anything is sent.
          </p>
        </div>
      </div>
    </PageContainer>
  );
}

function Stat({ value, label }) {
  return (
    <div className="rounded-2xl py-3" style={{ background: "rgba(255,255,255,0.09)" }}>
      <p className="text-[24px] leading-none" style={{ fontFamily: "Lora, serif" }}>{value}</p>
      <p className="text-[10px] mt-1 opacity-65">{label}</p>
    </div>
  );
}

function YouLink({ to, icon: Icon, title, note, last = false }) {
  return (
    <Link
      to={to}
      className="flex items-center gap-3 px-5 py-4 un-press"
      style={{ borderBottom: last ? "none" : "1px solid rgba(91,57,84,0.07)" }}
    >
      <span className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ background: "rgba(91,57,84,0.06)", color: "var(--un-plum)" }}>
        <Icon size={17} />
      </span>
      <span className="flex-1">
        <span className="block text-[14px] font-medium" style={{ color: "var(--un-text-1)" }}>{title}</span>
        <span className="block text-[11px]" style={{ color: "var(--un-text-3)" }}>{note}</span>
      </span>
      <ChevronRight size={16} style={{ color: "var(--un-text-3)" }} />
    </Link>
  );
}
function AmbientCareCard({ symbol, icon: Icon, title, note }) {
  if (!symbol) return null;
  return (
    <div
      className="rounded-[1.75rem] p-5"
      style={{
        background: "linear-gradient(135deg, rgba(243,232,220,0.82), rgba(235,216,214,0.72))",
        border: "1px solid rgba(91,57,84,0.055)",
      }}
    >
      <div className="flex items-center gap-2 mb-3">
        <span
          className="w-9 h-9 rounded-2xl flex items-center justify-center"
          style={{ background: "rgba(91,57,84,0.065)", color: "var(--un-plum)" }}
        >
          <Icon size={16} strokeWidth={1.7} />
        </span>
        <p
          className="text-[10px] uppercase tracking-[0.24em]"
          style={{ color: "var(--un-text-3)", fontFamily: "Manrope, sans-serif" }}
        >
          {symbol.value}
        </p>
      </div>
      <p className="text-[16px]" style={{ color: "var(--un-plum-deep)", fontFamily: "Lora, serif" }}>
        {title}
      </p>
      <p className="mt-1.5 text-[12px] leading-relaxed" style={{ color: "var(--un-text-3)" }}>
        {note}
      </p>
    </div>
  );
}
